import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { savePermitForm, updatePermitForm } from '../../store/slices/projectPermitsSlice';
import { showToast } from '../utils/Toast';
import { fetchProjectData, prefillFormData } from '../../utils/projectDataUtils';
import { printPermitForm, createCompanyHeader, formatFormField, formatTableRow } from '../../utils/PermitPrintUtils';
import FloatingActionBar from './FloatingActionBar';

const LumberForm = ({ projectId, currentPermit, onClose }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  
  // Define all the form fields based on the image
  const [formData, setFormData] = useState({
    projectName: '',
    jobAddress: '',
    jobNumber: '',
    date: '',
    items: {
      osb: { quantity: '', notes: '' },
      cdx: { quantity: '', notes: '' },
      tAndG: { quantity: '', notes: '' },
      shiplap: { quantity: '', notes: '' },
      fascia: { quantity: '', notes: '' },
      oneByX: { quantity: '', notes: '' },
      twoByFour: { quantity: '', notes: '' },
      twoByX: { quantity: '', notes: '' },
      twoByEight: { quantity: '', notes: '' },
      twoByTen: { quantity: '', notes: '' },
      twoByTwelve: { quantity: '', notes: '' },
      beam: { quantity: '', notes: '' },
      sevenDGunNails: { quantity: '', notes: '' },
      tenDGunNails: { quantity: '', notes: '' },
      shawdowBoard: { quantity: '', notes: '' }
    }
  });

  useEffect(() => {
    if (currentPermit && currentPermit.formType === 'lumber') {
      setFormData(currentPermit.formData || formData);
    } else {
      // Fetch project data to prefill the form
      const getProjectData = async () => {
        try {
          const projectData = await fetchProjectData(projectId);
          const prefilled = prefillFormData(projectData, formData);
          setFormData(prefilled);
        } catch (error) {
          console.error('Error prefilling form data:', error);
        }
      };
      
      getProjectData();
    }
  }, [currentPermit, projectId]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleItemChange = (itemKey, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: {
        ...prev.items,
        [itemKey]: {
          ...prev.items[itemKey],
          [field]: value
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (currentPermit && currentPermit._id) {
        await dispatch(updatePermitForm({
          projectId,
          permitId: currentPermit._id,
          formData: {
            ...currentPermit,
            formData
          }
        })).unwrap();
        showToast('Lumber form updated successfully', 'success');
      } else {
        await dispatch(savePermitForm({
          projectId,
          formType: 'lumber',
          formData
        })).unwrap();
        showToast('Lumber form saved successfully', 'success');
      }
      onClose();
    } catch (error) {
      showToast(error.message || 'Failed to save Lumber form', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format camelCase keys into readable labels
  const formatItemLabel = (key) => {
    // Special cases
    if (key === 'osb') return 'OSB';
    if (key === 'cdx') return 'CDX';
    if (key === 'tAndG') return 'T and G';
    if (key === 'shiplap') return 'Shiplap';
    if (key === 'fascia') return 'Fascia';
    if (key === 'oneByX') return '1x';
    if (key === 'twoByFour') return '2x4';
    if (key === 'twoByX') return '2x6';
    if (key === 'twoByEight') return '2x8';
    if (key === 'twoByTen') return '2x10';
    if (key === 'twoByTwelve') return '2x12';
    if (key === 'beam') return 'Beam';
    if (key === 'sevenDGunNails') return '7D Gun Nails';
    if (key === 'tenDGunNails') return '10D Gun Nails';
    if (key === 'shawdowBoard') return 'Shawdow Board';
    
    // General case: convert camelCase to spaces and capitalize
    const withSpaces = key.replace(/([A-Z])/g, ' $1').trim();
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  };

  const handleDownload = () => {
    // Create a printable version of the form content
    const formContent = `
      ${createCompanyHeader()}
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px;">
        <h1 class="form-title">WOOD</h1>
        <div style="width: 200px;">
          ${formatFormField('Date', formData.date)}
          ${formatFormField('Job Number', formData.jobNumber)}
        </div>
      </div>
      
      <div class="form-section">
        ${formatFormField('Project Name', formData.projectName)}
        ${formatFormField('Job Address', formData.jobAddress)}
      </div>
      
      <div class="form-section">
        <table class="form-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Quantity</th>
            </tr>
          </thead>
          <tbody>
            ${Object.entries(formData.items).map(([key, value]) => `
              <tr>
                <td>${formatItemLabel(key)}</td>
                <td>${value.quantity || ''}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;
    
    // Print the form
    printPermitForm(formData, 'Lumber (WOOD)', formContent);
  };

  return (
    <div className="permit-form-shell bg-white p-6 rounded-lg max-w-5xl mx-auto relative">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <img 
            src="/logo/topsail-logo-small.png" 
            alt="Topsail roofing" 
            className="h-16 mr-4"
          />
          <div>
            <h2 className="text-2xl font-bold">Topsail roofing</h2>
            <p className="text-sm">QLTY CRAFTSMANSHIP</p>
          </div>
        </div>
        <div className="text-right">
          <p>205 E Avenida Magdalena</p>
          <p>San Clemente, CA 92672</p>
          <p>Phone: (949) 370-8612</p>
          <p className="uppercase">topsailroofing.com</p>
          <p>CA 1070037</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="col-span-1">
          <h1 className="text-2xl font-bold">WOOD</h1>
        </div>
        <div className="col-span-1 grid grid-cols-2 gap-2">
          <div className="text-right pr-2">
            <div className="text-sm font-medium">Date:</div>
          </div>
          <div>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => handleChange('date', e.target.value)}
              className="w-full border border-gray-300 p-1 rounded"
            />
          </div>
          <div className="text-right pr-2">
            <div className="text-sm font-medium">Job Number:</div>
          </div>
          <div>
            <input
              type="text"
              value={formData.jobNumber}
              onChange={(e) => handleChange('jobNumber', e.target.value)}
              className="w-full border border-gray-300 p-1 rounded"
            />
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name:</label>
            <input
              type="text"
              value={formData.projectName}
              onChange={(e) => handleChange('projectName', e.target.value)}
              className="w-full border border-gray-300 p-2 rounded"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Job Address:</label>
            <input
              type="text"
              value={formData.jobAddress}
              onChange={(e) => handleChange('jobAddress', e.target.value)}
              className="w-full border border-gray-300 p-2 rounded"
            />
          </div>
        </div>

        <div className="overflow-x-auto mb-6">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr>
                <th className="border border-gray-300 p-2 w-1/3 text-center">Item</th>
                <th className="border border-gray-300 p-2 w-2/3 text-center">Quantity</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(formData.items).map(([key, value]) => (
                <tr key={key}>
                  <td className="border border-gray-300 p-2">
                    {formatItemLabel(key)}
                  </td>
                  <td className="border border-gray-300 p-2">
                    <input
                      type="text"
                      value={value.quantity}
                      onChange={(e) => handleItemChange(key, 'quantity', e.target.value)}
                      className="w-full p-1 border-0"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Add padding at the bottom to ensure content isn't hidden behind the floating bar */}
        <div className="pb-20"></div>
      </form>
      
      {/* Floating Action Bar */}
      <FloatingActionBar 
        onCancel={onClose}
        onDownload={handleDownload}
        onSubmit={handleSubmit}
        loading={loading}
        isUpdate={!!(currentPermit && currentPermit._id)}
      />
    </div>
  );
};

export default LumberForm;
